import {Component, DestroyRef, inject, OnInit} from "@angular/core";
import {Store} from "@ngrx/store";
import {AbstractControl, FormArray, FormBuilder, FormGroup, ValidationErrors, ValidatorFn, Validators} from "@angular/forms";
import {FundValuationDeferralActions} from "../../store/fund.actions";
import {FundValuationDeferral} from "../../models/fund-valuation-deferral";
import {MatDialogRef} from "@angular/material/dialog";
import {selectFundValuationDeferralData, selectSelectedFundValuationClosingDate} from "../../store/fund-valuation/fund-valuation.selectors";
import {EMPTY_MODEL_DATE, EMPTY_MONEY, EMPTY_PERCENT} from "../../../shared/model/traceable";
import Big from "big.js";
import {DecimalFormatPipe} from "../../../shared/pipes/decimal-format/decimal-format.pipe";
import {DateUtil} from "../../../shared/utils/date-util";
import {TraceableDate} from "../../generated-sources/api";
import {dateNotBeforeValidator} from "../../../shared/utils/form-validators";
import {FundValuationDeferralData} from "../../models/fund-valuation-deferral-data";
import {takeUntilDestroyed} from "@angular/core/rxjs-interop";

export interface FundValuationDeferralForm {
    id?: number | null;
    dueDate?: Date | null;
    amount?: number | null;
    percentage?: number | null;
}

export const byDueDateAsc = (a: FundValuationDeferralForm, b: FundValuationDeferralForm): number => {
    const aTime = a.dueDate ? a.dueDate.getTime() : Number.POSITIVE_INFINITY;
    const bTime = b.dueDate ? b.dueDate.getTime() : Number.POSITIVE_INFINITY;
    if (aTime === bTime) return 0;
    return aTime - bTime;
};

@Component({
    selector: "valumize-fund-valuation-deferral-dialog",
    standalone: false,
    templateUrl: "./fund-valuation-deferral-dialog.component.html",
    styleUrl: "./fund-valuation-deferral-dialog.component.scss"
})
export class FundValuationDeferralDialogComponent implements OnInit {
    private readonly store = inject(Store);
    private readonly fb = inject(FormBuilder);
    private readonly dialogRef = inject(MatDialogRef<FundValuationDeferralDialogComponent>);
    private readonly destroyRef = inject(DestroyRef);
    deferralForm = this.fb.group({
        includeDeferrals: this.fb.control<boolean>(false, {nonNullable: true}),
        deferrals: this.fb.array([], {validators: [this.totalPercentageMustBeOneIfRequired()]})
    });

    private seed: FundValuationDeferralForm[] = [];
    private closingDate?: TraceableDate;

    constructor() {
        this.dialogRef.disableClose = true; // Disable default close behavior
    }

    // --- Helpers ---
    get deferralsFA(): FormArray<FormGroup> {
        return this.deferralForm.get("deferrals") as FormArray<FormGroup>;
    }

    ngOnInit(): void {
        this.store.dispatch(FundValuationDeferralActions.load());

        this.store.select(selectFundValuationDeferralData)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(deferralData => {
                if (deferralData.status === "LOADED" || deferralData.status === "SAVED") {
                    this.deferralForm.controls.includeDeferrals.setValue(deferralData.data.includeDeferrals);
                    const deferrals = deferralData.data.fundValuationDeferrals
                        || [] as FundValuationDeferral[];
                    this.seed = this.convertToFormModel(deferrals).sort(byDueDateAsc);
                    if (this.seed.length > 0) {
                        this.setRows(this.seed);
                    }
                }
            });

        this.store.select(selectSelectedFundValuationClosingDate)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(closingDate => {
                this.closingDate = closingDate;
                this.deferralsFA.controls.forEach(r => r.get("dueDate")?.updateValueAndValidity({emitEvent: false}));
            });
    }

    save() {

        if (!this.deferralForm.valid) return;

        const value = this.deferralsFA.controls.map(g => g.getRawValue()) as FundValuationDeferralForm[];

        const fundValuationDeferralData: FundValuationDeferralData = (this.deferralForm.controls.includeDeferrals.value) ? {
            includeDeferrals: true,
            fundValuationDeferrals: this.convertToApiModel(value)
        } : {
            includeDeferrals: false,
            fundValuationDeferrals: []
        }

        this.store.dispatch(FundValuationDeferralActions.save({fundValuationDeferralData}));

        this.dialogRef.close();
    }

    cancel() {
        this.dialogRef.close();
    }

    // --- UI actions ---
    isSaveDisable(): boolean {
        return this.deferralForm.invalid ||
            (this.deferralForm.controls.includeDeferrals.value && this.deferralForm.controls.deferrals.length === 0);
    }

    add() {
        this.deferralsFA.push(this.createRow());
    }

    onIncludeDeferralsChange(includeDeferrals: boolean) {
        if (includeDeferrals) {
            this.setRows(this.seed);
        } else {
            this.deferralsFA.clear();
        }
    }

    remove(i: number) {
        this.deferralsFA.removeAt(i);
    }

    clearOther(i: number, edited: "amount" | "percentage") {
        const row = this.deferralsFA.at(i);
        if (!row) return;
        if (edited === "amount") row.get("percentage")?.setValue(null, {emitEvent: false});
        if (edited === "percentage") row.get("amount")?.setValue(null, {emitEvent: false});
        row.updateValueAndValidity({emitEvent: false});
    }

    reset() {
        this.setRows(this.seed);
        this.deferralForm.controls.includeDeferrals.setValue(this.seed.length > 0);
    }

    private createRow(d: Partial<FundValuationDeferralForm> = {}): FormGroup {
        return this.fb.group({
            id: this.fb.control<number | null>(d.id ?? null),
            dueDate: this.fb.control<Date | null>(d.dueDate ?? null, {validators: [Validators.required, this.closingDateAfterValidator]}),
            amount: this.fb.control<number | null>(d.amount ?? null),
            percentage: this.fb.control<number | null>(d.percentage ?? null, {validators: [Validators.required]}),
        });
    }

    private readonly closingDateAfterValidator: ValidatorFn = (control) => dateNotBeforeValidator(DateUtil.toJsDate(this.closingDate?.date))(control);

    private setRows(rows: FundValuationDeferralForm[]) {
        this.deferralsFA.clear({emitEvent: false});
        rows.forEach(r => this.deferralsFA.push(this.createRow(r), {emitEvent: false}));
        this.deferralsFA.controls.forEach(r => {
            const dueDate = r.get("dueDate");
            if (dueDate?.invalid) dueDate.markAsTouched();
        });
        this.deferralForm.markAsPristine();
    }

    private convertToFormModel(deferrals: FundValuationDeferral[]): FundValuationDeferralForm[] {
        return deferrals.map(d => ({
            id: d.id ?? null,
            dueDate: DateUtil.toJsDate(d.dueDate?.date) ? DateUtil.toJsDate(d.dueDate?.date) : null,
            amount: d.amount?.amount ?? null,
            percentage: DecimalFormatPipe.transformFractionToPercent(d.percentage?.fraction) ?? null,
        } as FundValuationDeferralForm));
    }

    private convertToApiModel(deferrals: FundValuationDeferralForm[]): FundValuationDeferral[] {
        return deferrals.map(d => ({
            id: d.id ?? undefined,
            dueDate: DateUtil.toIsoDate(d.dueDate) ? {
                ...EMPTY_MODEL_DATE,
                date: DateUtil.toIsoDate(d.dueDate)
            } : EMPTY_MODEL_DATE,
            amount: d.amount != null ? {...EMPTY_MONEY, amount: d.amount} : EMPTY_MONEY,
            percentage: d.percentage != null ? {
                ...EMPTY_PERCENT,
                fraction: DecimalFormatPipe.transformPercentToFraction(d.percentage)
            } : EMPTY_PERCENT,
        } as FundValuationDeferral));
    }

    // --- Validator ---
    private totalPercentageMustBeOneIfRequired(): ValidatorFn {
        return (control: AbstractControl): ValidationErrors | null => {
            const fa = control as FormArray;
            if (!fa?.controls?.length) return null;

            // Sum numbers safely (treat null/NaN as 0)
            const total = fa.controls.reduce((acc, c) => {
                const v = new Big(c.get("percentage")?.value ?? new Big(0));
                return acc.plus(v);
            }, new Big(0));

            return total.eq(100) ? null : {totalNotOne: true};
        };
    }
}
